import { useTheme } from 'styled-components';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';

import { Typography } from '@libs/components';
import {
  getAmountPrefixAndColor,
  getPercentageWithSymbol,
  formatLargeAmount,
} from '@libs/helpers';
import {
  CurrencySymbol,
  CurrencyType,
  HistoryPeriodEnumKeys,
} from '@libs/types';

import { historyPeriodLabels } from 'src/constants';
import { getAlternativeAssetLabel } from 'src/helpers';
import { useTranslation } from 'src/hooks';
import { InfoToolTip } from 'src/components';
import { CoinMarketCapAsset } from 'src/types';

import {
  ContentWrapper,
  CustomTypography,
  ProgressBarWrapper,
  Row,
  RowLeftComponentWrapper,
  StatisticsWrapper,
  TitleAndDescriptionWrapper,
  StatRow,
} from './styles';

type CoinStatisticsProps = {
  assetCode: CurrencyType;
  assetInfo?: CoinMarketCapAsset;
};
const CoinStatistics: React.FC<CoinStatisticsProps> = ({
  assetCode,
  assetInfo,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const styles = buildStyles({
    trailColor: colors.borders.default,
    pathColor: colors.text.default,
  });

  const currSymbol = CurrencySymbol.USD;

  const periodText = `(${t(
    historyPeriodLabels[HistoryPeriodEnumKeys.ONE_DAY]
  )})`;

  const { absoluteAmount } = getAmountPrefixAndColor(
    colors,
    assetInfo?.volmktcap24h
  );

  const {
    prefix: marketCapPrefix,
    changeColor: marketCapColor,
    absoluteAmount: marketCapAbsoluteAmount,
  } = getAmountPrefixAndColor(colors, assetInfo?.marketCapChange);

  const {
    prefix: volumePrefix,
    changeColor: volumeColor,
    absoluteAmount: volumeChange24h,
  } = getAmountPrefixAndColor(colors, assetInfo?.volumeChange24h);

  const assetLabel = getAlternativeAssetLabel(assetCode);

  const data = [
    {
      label: 'Market cap',
      tooltipText: t('coin_details_tooltip_marketcap'),
      rightTitle: formatLargeAmount(assetInfo?.marketCap, currSymbol),
      rightDescription: (
        <CustomTypography
          type="bodyButton"
          fontFamily="primaryMedium"
          color={marketCapColor}
        >
          {marketCapPrefix}
          {marketCapAbsoluteAmount}%
        </CustomTypography>
      ),
    },
    {
      label: 'FDV',
      tooltipText: t('coin_details_tooltip_fdv'),
      rightTitle: formatLargeAmount(assetInfo?.fdv, currSymbol),
    },
    {
      label: 'Total supply',
      tooltipText: t('coin_details_tooltip_total_supply'),
      rightTitle: formatLargeAmount(assetInfo?.totalSupply, currSymbol),
    },
    {
      label: 'Volume ' + periodText,
      tooltipText: t('coin_details_tooltip_volume'),
      rightTitle: formatLargeAmount(assetInfo?.volume24h, currSymbol),
      rightDescription: (
        <CustomTypography
          type="bodyButton"
          color={volumeColor}
          fontFamily="primaryMedium"
        >
          {volumePrefix}
          {volumeChange24h}%
        </CustomTypography>
      ),
    },
    {
      label: 'Vol/Mkt Cap ' + periodText,
      tooltipText: t('coin_details_tooltip_volume_market_cap'),
      rightTitle: getPercentageWithSymbol(absoluteAmount.toFixed(4)),
    },
    {
      label: 'Max. supply',
      tooltipText: t('coin_details_tooltip_max_supply'),
      rightTitle: formatLargeAmount(assetInfo?.maxSupply) + ` ${assetLabel}`,
    },
    {
      label: 'Circulating supply',
      tooltipText: t('coin_details_tooltip_circulating_supply'),
      rightTitle: (
        <Row>
          <Typography type="subHeadline" fontFamily="primaryBold">
            {formatLargeAmount(assetInfo?.circulatingSupply)} {assetLabel}
          </Typography>
          {assetInfo?.circulatingSupply && assetInfo?.maxSupply && (
            <ProgressBarWrapper>
              <CircularProgressbar
                styles={styles}
                strokeWidth={15}
                value={
                  (assetInfo?.circulatingSupply * 100) / assetInfo?.maxSupply
                }
                counterClockwise
              />
            </ProgressBarWrapper>
          )}
        </Row>
      ),
    },
  ];

  return (
    <ContentWrapper>
      <Typography type="subHeadline" fontFamily="primaryBold">
        {t('coin_details_asset_common_stats', { assetName: assetLabel })}
      </Typography>

      <StatisticsWrapper>
        {data.map((item, index) => {
          const isLast = index === data.length - 1;
          return (
            <StatRow hasBorder={!isLast} key={index}>
              <RowLeftComponentWrapper>
                <InfoToolTip text={item.tooltipText} />
                <Typography
                  type="bodyButton"
                  fontFamily="primaryMedium"
                  color={colors.text.secondary}
                >
                  {item.label}
                </Typography>
              </RowLeftComponentWrapper>

              <TitleAndDescriptionWrapper>
                <Typography type="subHeadline" fontFamily="primaryBold">
                  {item.rightTitle}
                </Typography>
                {item.rightDescription}
              </TitleAndDescriptionWrapper>
            </StatRow>
          );
        })}
      </StatisticsWrapper>
    </ContentWrapper>
  );
};

export default CoinStatistics;
